import { mergeStyles, Modal, PrimaryButton, Stack, useTheme } from '@fluentui/react';
import React, { useEffect, useRef } from 'react';
import { BaseComponentProps, Input, RemoteMessage } from '..';

export const PatientAuthorization = (props: BaseComponentProps): JSX.Element => {
  const { onSubmit, inputs } = props;
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = React.useState(true);
  const [isButtonsDisabled, setIsButtonsDisabled] = React.useState(true);
  const [patientAuthorizationChecked, setPatientAuthorizationChecked] = React.useState(false);
  const width = Math.min(window.innerWidth, window.outerWidth) - 64;
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Diasble body scroll when Modal open
    document.body.style.overflow = 'hidden';

    return () => {
      // Enable body scroll when Modal unmounts
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const handleCheckboxChange = () => {
    setPatientAuthorizationChecked(!patientAuthorizationChecked);
  };

  useEffect(() => {
    if (patientAuthorizationChecked) setIsButtonsDisabled(false);
    else setIsButtonsDisabled(true);
  }, [patientAuthorizationChecked]);

  const handleOnSubmit = (input: Input) => {
    setIsModalOpen(false);
    onSubmit && onSubmit([input]);
  };

  if (!isModalOpen) {
    return (
      <RemoteMessage
        text={
          <a
            style={{
              cursor: 'pointer',
              color: theme.palette.white,
              textDecoration: 'underline',
            }}
            onClick={() => setIsModalOpen(true)}
          >
            {inputs?.find((i) => i.name === 'title')?.value}
          </a>
        }
      ></RemoteMessage>
    );
  }

  return (
    <Modal
      isOpen={isModalOpen}
      styles={{
        main: {
          borderRadius: theme.effects.roundedCorner6,
          overflowY: 'scroll',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      allowTouchBodyScroll={true}
    >
      <div
        className={mergeStyles({
          padding: '1rem',
          boxShadow: theme.effects.elevation8,
          width: width,
          borderRadius: `${theme.effects.roundedCorner6} ${theme.effects.roundedCorner6} 0 0`,
          background: theme.palette.neutralPrimary,
        })}
      >
        <Stack
          style={{
            height: '100%',
            color: 'black',
            paddingBottom: '0rem',
            overflowY: 'scroll',
          }}
        >
          <Stack horizontal horizontalAlign="start">
            <h3
              style={{
                color: 'black',
                fontSize: '14px',
                margin: 0,
                paddingBottom: '0.5rem',
              }}
            >
              {inputs?.find((i) => i.name === 'title')?.value}
            </h3>
          </Stack>
          <div
            ref={modalContentRef}
            style={{
              maxHeight: window.innerHeight / 1.8,
              overflow: 'auto',
              paddingBottom: '1rem',
              fontSize: '11px',
              fontWeight: 400,
            }}
          >
            {inputs?.find((i) => i.name === 'body')?.valueJSX}
            <Stack
              horizontal
              horizontalAlign="start"
              verticalAlign="center"
              style={{ display: 'inline-flex' }}
            >
              <input
                type="checkbox"
                id="patientAuthorization"
                name="patientAuthorization"
                checked={patientAuthorizationChecked}
                onChange={handleCheckboxChange}
                style={{
                  minWidth: '1.5rem',
                  minHeight: '1.5rem',
                  borderRadius: theme.effects.roundedCorner6,
                  outline: 'none',
                }}
              />
              <Stack horizontalAlign="start" verticalAlign="center">
                <label htmlFor="textTerms" style={{ marginLeft: '0.5rem' }}>
                  {inputs?.find((i) => i.name === 'checkbox')?.valueJSX}
                </label>
              </Stack>
            </Stack>
          </div>
        </Stack>

        <Stack horizontal horizontalAlign="center" style={{ padding: '1rem' }}>
          <PrimaryButton
            className={mergeStyles({
              height: '2.5rem',
              minWidth: '8rem',
              borderRadius: theme.effects.roundedCorner6,
              boxShadow: theme.effects.elevation8,
              fontSize: '16px',
              color: theme.palette.neutralLight,
              background: theme.palette.themePrimary,
              ':disabled': {
                background: '#faecc0',
                color: '#b3b3b3',
              },
            })}
            disabled={isButtonsDisabled}
            onClick={() =>
              handleOnSubmit({
                id: props?.inputs[0]?.name,
                name: 'submit',
                key: props?.inputs[0]?.key,
                value: 'Submit',
              })
            }
          >
            Submit
          </PrimaryButton>
        </Stack>
      </div>
    </Modal>
  );
};
